<template>
  <div class="arcgis-wizard-container">
    <div class="wizard-nav">
      <div :class="['step-item', { active: currentArcgisStep === 1 }]">1. Toc</div>

      <div :class="['step-item', { active: currentArcgisStep === 2 }]">2. Campos</div>
    </div>

    <div class="wizard-content">
      <component :is="currentStepComponent" ref="currentStep" v-model="currentDataSegment" :categories="categories" />

      <div class="container-action-left" style="justify-content: end; margin-top: 20px">
        <button class="btn btn-secondary" @click="prevStep" :disabled="currentArcgisStep === 1 || isLoading">
          Anterior
        </button>

        <button class="btn btn-primary" @click="nextStep" :disabled="isLoading">
          <span v-if="isLoading">Cargando...</span>
          <span v-else>{{ currentArcgisStep === 2 ? "Finalizar Paso" : "Siguiente" }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import FormularioArcgisPaso1 from "./FormularioArcgisPaso1.vue";
import FormularioArcgisPaso2 from "./FormularioArcgisPaso2.vue";
import { restLayer } from "@/components/mixin/restLayer.js";
import { formatter } from "@/components/mixin/formatter.js";

export default {
  name: "FormularioArcgis",
  mixins: [formatter, restLayer],
  components: {
    FormularioArcgisPaso1,
    FormularioArcgisPaso2,
  },

  props: {
    categories: Array,
    layerCreationMode: String,
    isEdit: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      currentArcgisStep: 1,
      isLoading: false,

      formData: {
        nombre: "",
        descripcion: "",
        url: "",
        codigo: "",
        fuente: "",

        esVulnerable: "NO",
        capPoblacion: "NO",
        orden: 0,
        categoria: "-1",
      },
      fieldsArcgis: [],
    };
  },
  
  watch: {
    initialData: {
      handler(newVal) {
        if (this.isEdit && newVal) {
          this.populateForm(newVal);
        }
      },
      deep: true,
      immediate: true,
    }
  },

  computed: {
    currentStepComponent() {
      return this.currentArcgisStep === 1
        ? "FormularioArcgisPaso1"
        : "FormularioArcgisPaso2";
    },
    currentDataSegment: {
      get() {
        return this.currentArcgisStep === 1 ? this.formData : this.fieldsArcgis;
      },
      set(newValue) {
        if (this.currentArcgisStep === 1) {
          this.formData = newValue;
        } else {
          this.fieldsArcgis = newValue;
        }
      },
    },
  },

  methods: {
    populateForm(data) {
        this.formData = {
            nombre: data.name || "",
            descripcion: data.desc || "",
            url: data.provider ? data.provider.url : "",
            codigo: data.codigo || "",
            fuente: data.fuente || "",
            esVulnerable: data.esVulnerable || data.es_vulnerable || "NO", // Handle both casing if unstable
            capPoblacion: data.capPoblacion || "NO",
            orden: data.orden ? parseInt(data.orden) : 0,
            categoria: data.id_category || "-1",
        };
        // Populate fields if available, otherwise they might need to be fetched or ignored
        if (data.campos) {
            this.fieldsArcgis = data.campos.map(f => ({
                name: f.name,
                alias: f.alias,
                order: f.order,
                visible: f.visible,
                tooltip: f.tooltip,
                reporte: f.reporte,
                titulo: f.titulo
            }));
        }
    },
    async nextStep() {
      console.log("Next step clicked", this.formData);

      if (this.currentArcgisStep === 1) {
        // --- VALIDACIONES PASO 1 ---
        const { nombre, descripcion, orden, categoria, url, esVulnerable } = this.formData;

        if (!nombre || !descripcion) {
          alert("Por favor, complete todos los campos obligatorios (Nombre, Descripción, Orden).");
          return;
        }

        if (!categoria || categoria === "-1") {
          alert("Debe seleccionar una categoría.");
          return;
        }

        // Validación URL idéntica al componente
        if (!url) {
          alert("Debe ingresar una URL obligatoria.");
          return;
        }
        if (!/^https?:\/\//i.test(url)) {
          alert("La URL debe comenzar con http:// o https://");
          return;
        }
        if (url.endsWith("/")) {
          alert("La URL no debe terminar en '/' (slash)");
          return;
        }
        if (/\/\d+$/.test(url)) {
          alert("La URL no debe terminar con el índice (ej: /0). Colóquelo en el campo 'Código'.");
          return;
        }

        // --- Fin Validaciones ---

        // --- Lógica del Paso 1: Cargar campos desde ArcGIS ---
        // Si estamos editando y ya tenemos campos, quizás no necesitemos recargarlos a menos que la URL cambie?
        // Por ahora, mantenemos comportamiento simple: Si hay campos cargados, pasamos. Si no, cargamos.
        if (this.isEdit && this.fieldsArcgis.length > 0 && this.formData.url === this.initialData.provider.url) {
             this.currentArcgisStep++;
             return;
        }

        this.isLoading = true;
        try {
          // Construir URL: url + /codigo + ?f=json
          let fetchUrl = this.formData.url;
          if (this.formData.codigo) {
            fetchUrl += "/" + this.formData.codigo;
          }
          fetchUrl += "?f=json";

          console.log("Fetching fields from:", fetchUrl);

          const response = await this.$http.get(fetchUrl);
          const data = response.data;

          if (data.error) {
            throw new Error(data.error.message || "Error devuelto por ArcGIS.");
          }

          if (!data.fields || !Array.isArray(data.fields)) {
            // Intento de fallback
            throw new Error("No se encontraron 'fields' en la respuesta JSON.");
          }

          // Mapear campos
          this.fieldsArcgis = data.fields.map((f, index) => ({
             name: f.name,
             alias: f.alias || f.name,
             order: index + 1,
             visible: true,
             tooltip: false,
             reporte: false,
             titulo: false,
           }));

          console.log("Campos cargados:", this.fieldsArcgis);
          this.currentArcgisStep++;

        } catch (error) {
          console.error("Error fetching ArcGIS fields:", error);
          alert("Error al obtener campos de la capa: " + error.message);
        } finally {
          this.isLoading = false;
        }

      } else {
        const finalData = this.getData();
        console.log("✅ DATOS FINALES LISTOS PARA ENVIAR (Paso 2):");
        console.log("TOC (Paso 1):", finalData.toc);
        console.log("CAMPOS (Paso 2 - Tabla Editada):", finalData.campos);
        
        if (this.isEdit) {
            this.putLayerArcgis(finalData);
        } else {
            this.postLayerArcgis(finalData);
        }
      }
    },

    prevStep() {
      if (this.currentArcgisStep > 1) this.currentArcgisStep--;
    },

    getData() {
      return {
        toc: { ...this.formData },
        campos: [...this.fieldsArcgis],
      };
    },
  },
};
</script>

<style scoped>
.arcgis-wizard-container {
  display: flex;
  gap: 20px;
  width: 100%;
}

.wizard-content {
  flex-grow: 1;
  min-width: 0;
}

.step-item {
  padding: 10px;
  margin-bottom: 5px;
  cursor: pointer;
  background-color: #eee;
  border: 1px solid #ddd;
}

.step-item.active {
  background-color: #006fb3;
  color: white;
  font-weight: bold;
}
</style>
